/* eslint-disable no-console */
import { useEffect, useMemo, useRef } from 'react';

// Global cache for object URLs to prevent duplicates
const objectUrlCache = new Map<string, { url: string; refCount: number }>();

export type UseObjectUrlOptions = {
    file: File | null;
    // Optional cache key for deduplication. If not provided, uses file.name + file.size + file.lastModified
    cacheKey?: string;
    enabled?: boolean;
};

export type UseObjectUrlResult = {
    url: string | null;
};

export function useObjectUrl({ file, cacheKey, enabled = true }: UseObjectUrlOptions): UseObjectUrlResult {
    const cleanupRef = useRef<(() => void) | null>(null);

    console.log('[useObjectUrl] Hook called with:', {
        fileName: file?.name,
        fileSize: file?.size,
        cacheKey,
        enabled,
    });

    // Generate cache key if not provided
    const effectiveCacheKey = useMemo(() => {
        if (cacheKey) {
            console.log('[useObjectUrl] Using provided cache key:', cacheKey);
            return cacheKey;
        }
        if (!file) {
            console.log('[useObjectUrl] No file provided, cache key is null');
            return null;
        }

        // Create a unique key based on file properties
        const generatedKey = `${file.name}-${file.size}-${file.lastModified}`;
        console.log('[useObjectUrl] Generated cache key:', generatedKey);
        return generatedKey;
    }, [file, cacheKey]);

    // Create object URL and manage cache
    const url = useMemo(() => {
        if (!file || !effectiveCacheKey || !enabled) {
            console.log('[useObjectUrl] Skipping URL creation:', {
                hasFile: !!file,
                hasCacheKey: !!effectiveCacheKey,
                enabled,
            });
            return null;
        }

        // Always create a new object URL for the current file
        // The cache is used for deduplication across different components using the same file
        try {
            console.log('[useObjectUrl] Creating new object URL for cache key:', effectiveCacheKey);
            const newUrl = URL.createObjectURL(file);

            // Update cache with new URL
            const cached = objectUrlCache.get(effectiveCacheKey);
            if (cached) {
                // Revoke the old URL before replacing it
                URL.revokeObjectURL(cached.url);
                cached.url = newUrl;
                console.log('[useObjectUrl] Updated cached URL, refCount:', cached.refCount);
            }
            else {
                objectUrlCache.set(effectiveCacheKey, {
                    url: newUrl,
                    refCount: 1,
                });
                console.log('[useObjectUrl] New object URL created and cached:', newUrl);
            }

            return newUrl;
        }
        catch (error) {
            console.error('[useObjectUrl] Error creating object URL:', error);
            return null;
        }
    }, [file, effectiveCacheKey, enabled]);

    // Cleanup function
    const cleanup = useMemo(() => {
        if (!effectiveCacheKey) {
            console.log('[useObjectUrl] No cleanup needed - no effective cache key');
            return null;
        }

        return () => {
            console.log('[useObjectUrl] Cleanup function called for cache key:', effectiveCacheKey);
            const cached = objectUrlCache.get(effectiveCacheKey);
            if (cached) {
                cached.refCount--;
                console.log('[useObjectUrl] RefCount decreased to:', cached.refCount);
                if (cached.refCount <= 0) {
                    console.log('[useObjectUrl] Revoking object URL:', cached.url);
                    URL.revokeObjectURL(cached.url);
                    objectUrlCache.delete(effectiveCacheKey);
                    console.log('[useObjectUrl] Object URL revoked and removed from cache');
                }
            }
            else {
                console.warn('[useObjectUrl] Expected error: Cache entry not found for cleanup:', effectiveCacheKey);
            }
        };
    }, [effectiveCacheKey]);

    // Store cleanup function in ref for useEffect
    cleanupRef.current = cleanup;

    // Cleanup on unmount or when file/cacheKey changes
    useEffect(() => {
        console.log('[useObjectUrl] Effect triggered, effectiveCacheKey:', effectiveCacheKey);

        // Increment ref count when component mounts or cache key changes
        if (effectiveCacheKey) {
            const cached = objectUrlCache.get(effectiveCacheKey);
            if (cached) {
                cached.refCount++;
                console.log('[useObjectUrl] Incremented ref count to:', cached.refCount);
            }
        }

        return () => {
            if (cleanupRef.current) {
                console.log('[useObjectUrl] Component unmounting, calling cleanup');
                cleanupRef.current();
            }
            else {
                console.warn('[useObjectUrl] Expected error: No cleanup function available on unmount');
            }
        };
    }, [effectiveCacheKey]);

    console.log('[useObjectUrl] Returning URL:', url);
    return {
        url,
    };
}
